/**
 * Grundrisse (10-web-admin § 5).
 *
 * The overview and the rebuild calendar. The calendar is the reason this screen
 * exists: it answers "when can we take desks out of service" with numbers
 * rather than a guess, and it keeps the certain days visually separate from the
 * estimated ones so nobody schedules a rebuild over bookings that already exist.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useNavigate } from 'react-router';
import { Button, Card, StateScreen } from '@cowoo/shared/ui-web';
import {
  ApiProblem,
  calendarBars,
  formatDayShort,
  formatTime,
  todayBerlin,
  type CalendarBar,
} from '@cowoo/shared';
import { api } from '../../lib/api';
import { Dialog, Field, Icon, IconTile, fieldClass } from '../../ui';
import { TodayAndBlocks } from './TodayAndBlocks';

interface AdminLocation {
  id: string;
  name: string;
  status: string;
}

interface PlanCard {
  id: string;
  locationId: string;
  name: string;
  published: { id: string; version: number; publishedAt: string | null } | null;
  draft: { id: string; version: number; rev: number; updatedAt: string } | null;
  hasImage: boolean;
  /** Resource count per kind on the version the card is about (§ 5.1). */
  inventory: Record<string, number>;
  zones: string[];
  imageUrl: string | null;
}

interface RebuildResponse {
  days: Array<{ day: string; booked: number; expected: number | null }>;
  freeWindows: Array<{ from: string; to: string; roomBookings: number }>;
}

export function Plans() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [from, setFrom] = useState(todayBerlin());
  const [locationId, setLocationId] = useState<string | null>(null);
  /** § 5.3 — "Sperrung anlegen" on a free window opens the block dialog on it. */
  const [blockPrefill, setBlockPrefill] = useState<{ from: string; to: string; nonce: number } | null>(null);

  /**
   * One query key, one shape. Four screens read `['admin','locations']` and
   * this one cached the whole envelope while the others cached `items`, so
   * whichever mounted first decided whether the next `…data.find(…)` was a
   * function — the sticker dialog crashed on the router's error page when it
   * was opened straight from the Grundrisse list and worked on a reload.
   */
  const locations = useQuery({
    queryKey: ['admin', 'locations'],
    queryFn: async () => {
      const { data, error } = await api.GET('/admin/locations', {});
      if (error) throw error;
      return data.items;
    },
  });

  /**
   * Every building, not only the open ones.
   *
   * The rebuild calendar asks an open location what is booked, so its switcher
   * is rightly filtered. The *cards* are not: a plan of the Loft is drawn long
   * before the Loft opens, and looking its name up in the open list left every
   * one of them titled " · Erdgeschoss".
   */
  const all = (locations.data ?? []) as AdminLocation[];
  const open = all.filter((location) => location.status === 'open');
  const active = locationId ?? open[0]?.id ?? null;

  const plans = useQuery({
    queryKey: ['admin', 'floor-plans'],
    queryFn: async () => {
      const { data, error } = await api.GET('/admin/floor-plans', {});
      if (error) throw error;
      return data.items;
    },
  });

  const rebuild = useQuery({
    queryKey: ['admin', 'rebuild', active, from],
    enabled: active !== null,
    queryFn: async () => {
      const { data, error } = await api.GET('/admin/locations/{id}/rebuild-calendar', {
        params: { path: { id: active as string }, query: { from } },
      });
      if (error) throw error;
      return data as unknown as RebuildResponse;
    },
  });

  const bars = calendarBars(rebuild.data?.days ?? [], rebuild.data?.freeWindows ?? []);
  const window = rebuild.data?.freeWindows[0] ?? null;

  if (locations.isError) return <StateScreen title={t('admin.plans.title')} />;

  return (
    <div className="flex flex-col gap-6 p-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-h1 text-ink">{t('admin.plans.title')}</h1>
          {/* § 5.1's "Standort {{group}}" is the operator's location group —
              one of them in v1, which is the same name the shell already
              carries. `plans` counts plans, not buildings, which is what it
              was counting. */}
          <p className="font-body text-muted text-sm">
            {t('admin.plans.sub', {
              group: t('admin.shell.location'),
              buildings: all.length,
              plans: plans.data?.length ?? 0,
            })}
          </p>
        </div>
        <Button size="md" onClick={() => void navigate('/grundrisse/neu')}>
          <Icon name="plus" size={16} />
          {t('admin.plans.create')}
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {(plans.data ?? []).map((plan: PlanCard) => (
          <PlanCardView
            key={plan.id}
            plan={plan}
            locationName={all.find((location) => location.id === plan.locationId)?.name ?? ''}
          />
        ))}
      </div>

      <TodayAndBlocks locationId={active} prefill={blockPrefill} />

      <Card>
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h2 className="font-heading text-ink text-lg">{t('admin.plans.rebuildTitle')}</h2>
            {/* § 5.3 — the calendar is about one building, and with a second
                one planned the card has to say which. */}
            <p className="font-body text-muted text-sm">
              {t('admin.plans.rebuildSub', {
                location: all.find((location) => location.id === active)?.name ?? '',
              })}
            </p>
          </div>
          <div className="flex gap-1">
            <IconTile
              icon="chevronLeft"
              size={36}
              label={t('admin.plans.earlier')}
              onClick={() => setFrom(shift(from, -7))}
            />
            <IconTile
              icon="chevronRight"
              size={36}
              label={t('admin.plans.later')}
              onClick={() => setFrom(shift(from, 7))}
            />
          </div>
        </div>

        {open.length > 1 ? (
          <div className="mb-4 flex gap-2">
            {open.map((location: AdminLocation) => (
              <Button
                key={location.id}
                size="sm"
                variant={location.id === active ? 'primary' : 'secondary'}
                onClick={() => setLocationId(location.id)}
              >
                {location.name}
              </Button>
            ))}
          </div>
        ) : null}

        <Calendar bars={bars} />

        <div className="font-body text-muted mt-3 flex gap-4 text-xs">
          <Legend className="bg-teal" label={t('admin.plans.legendCertain')} />
          <Legend className="bg-teal/40" label={t('admin.plans.legendExpected')} />
          <Legend className="bg-lime" label={t('admin.plans.legendFree')} />
        </div>

        {window ? (
          <div className="bg-fog rounded-card mt-4 flex gap-3 p-4">
            <Icon name="sparkle" size={18} className="text-teal mt-0.5 flex-none" />
            <div className="flex-1">
              <p className="font-body text-ink font-semibold">
                {t('admin.plans.windowTitle', {
                  from: formatDayShort(window.from),
                  to: formatDayShort(window.to),
                })}
              </p>
              {/* § 5.3 — a window is free of *desk* bookings; a meeting in it is
                still a reason to start after lunch. */}
              <p className="font-body text-muted mt-1 text-sm">
                {t('admin.plans.windowRooms', { count: window.roomBookings })}
              </p>
              <div className="mt-3 flex gap-2">
                <Button
                  variant="secondary"
                  onClick={() => setBlockPrefill({ from: window.from, to: window.to, nonce: Date.now() })}
                >
                  {t('admin.plans.createBlock')}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => void navigate(`/buchungen?from=${window.from}&to=${window.to}`)}
                >
                  {t('admin.plans.viewBookings')}
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <p className="font-body text-muted mt-4 text-sm">{t('admin.plans.noWindow')}</p>
        )}
      </Card>
    </div>
  );
}

/**
 * § 5.1 — the four card states. Which actions a plan offers follows entirely
 * from whether it has an image, a draft and a published version, so the state
 * is derived rather than stored.
 */
function PlanCardView({ plan, locationName }: { plan: PlanCard; locationName: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(plan.name);
  const [problem, setProblem] = useState<ApiProblem | null>(null);
  const state = planState(plan);
  const cardName = t('admin.plans.cardName', { location: locationName, floor: plan.name });

  /**
   * § 5.1 — "Entwurf mit {{n}} Änderungen", `n` from the diff.
   *
   * Only asked where there is a draft to compare: for a published version the
   * answer is about the version before it, which is the sticker sheet's
   * question, not this chip's.
   */
  const changes = useQuery({
    queryKey: ['admin', 'plan-diff', plan.draft?.id],
    enabled: plan.draft !== null && plan.published !== null,
    queryFn: async () => {
      const { data, error } = await api.GET('/admin/floor-plan-versions/{vid}/diff', {
        params: { path: { vid: plan.draft!.id } },
      });
      if (error) throw error;
      return data.changes.length;
    },
  });

  /**
   * The name is the one thing about a plan that carries nothing: bookings,
   * stickers and the door all key off the resource and its `desk_code`. So
   * renaming needs none of the guards deleting needs, and a typo on a
   * published plan stops being permanent.
   */
  const rename = useMutation({
    mutationFn: async () => {
      const { error } = await api.PATCH('/admin/floor-plans/{id}', {
        params: { path: { id: plan.id } },
        body: { name: name.trim() },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      setRenaming(false);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'floor-plans'] });
    },
    onError: (error) => setProblem(error instanceof ApiProblem ? error : null),
  });

  /**
   * § 5.1 — only an empty plan can be removed, and the server decides.
   *
   * The button is hidden once a plan has been published because the server
   * refuses that anyway: its desks carry bookings and the printed stickers name
   * their `desk_code`. Hiding it is the courtesy; the refusal is the rule.
   */
  const remove = useMutation({
    mutationFn: async () => {
      const { error } = await api.DELETE('/admin/floor-plans/{id}', {
        params: { path: { id: plan.id } },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      setConfirmDelete(false);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'floor-plans'] });
    },
    onError: (error) => setProblem(error instanceof ApiProblem ? error : null),
  });

  return (
    <Card
      className={state === 'no_image' ? 'border-line border-2 border-dashed' : ''}
      data-testid="plan-card"
    >
      {/* § 5.1 — the thumbnail, with the zones it shows named underneath.
          The picture is an image, so what it says is not readable by anything
          but an eye; the zone list is the same information in words. */}
      {plan.imageUrl ? (
        <div className="rounded-input border-line mb-3 overflow-hidden border">
          <img src={plan.imageUrl} alt="" className="bg-fog h-[120px] w-full object-contain" />
        </div>
      ) : (
        // § 5.1's dashed card: the picture that is not there yet, drawn as
        // the board draws it, so the card keeps the shape of its neighbours.
        <div className="rounded-input border-line text-placeholder mb-3 flex h-[120px] items-center justify-center border border-dashed">
          <Icon name="image" size={28} />
        </div>
      )}
      {plan.zones.length > 0 ? (
        <p className="font-body text-muted mb-2 text-xs">{plan.zones.join(' · ')}</p>
      ) : null}

      <p className="font-heading text-ink" data-testid="plan-name">
        {cardName}
      </p>

      <p className="font-body text-muted mt-1 text-sm">
        {state === 'no_image'
          ? t('admin.plans.stateNoImageBody')
          : plan.published
            ? t('admin.plans.inventory', {
                parts: inventoryLine(plan.inventory, t),
                version: plan.published.version,
                date: plan.published.publishedAt
                  ? formatDayShort(plan.published.publishedAt.slice(0, 10))
                  : '',
              })
            : t('admin.plans.inventoryDraft', {
                parts: inventoryLine(plan.inventory, t),
                version: plan.draft?.version ?? 0,
              })}
      </p>

      {plan.draft && state !== 'no_image' ? (
        <p className="font-body text-muted text-sm">
          {t('admin.plans.draftSince', { time: formatTime(plan.draft.updatedAt) })}
        </p>
      ) : null}

      <div className="mt-3 flex flex-wrap gap-2">
        <Chip state={state} />
        {state === 'published_with_draft' ? (
          <span className="bg-warn-bg font-body text-warn-text rounded-full px-3 py-1 text-xs font-semibold">
            {t('admin.plans.stateDraftChanges', { count: changes.data ?? 0 })}
          </span>
        ) : null}
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {state === 'no_image' ? (
          <Button onClick={() => void navigate(`/grundrisse/${plan.id}/upload`)}>
            {t('admin.plans.upload')}
          </Button>
        ) : null}
        {plan.published ? (
          <Button variant="secondary" onClick={() => void navigate(`/grundrisse/${plan.id}/aufkleber`)}>
            <Icon name="sticker" size={16} />
            {t('admin.plans.stickers')}
          </Button>
        ) : null}
        {plan.draft && state !== 'no_image' ? (
          <Button onClick={() => void navigate(`/grundrisse/${plan.id}/editor`)}>
            {t('admin.plans.openDraft')}
          </Button>
        ) : plan.published ? (
          // "Bearbeiten" creates the draft from the published version first.
          <Button onClick={() => void navigate(`/grundrisse/${plan.id}/editor?create=1`)}>
            {t('admin.plans.edit')}
          </Button>
        ) : null}
        <Button variant="ghost" onClick={() => setRenaming(true)}>
          {t('admin.plans.rename')}
        </Button>
        {/* § 6.2's "Ersetzen" lives on the upload screen, and until now there
            was no way back to it once a plan had a picture. */}
        {state === 'no_image' ? null : (
          <Button variant="ghost" onClick={() => void navigate(`/grundrisse/${plan.id}/upload`)}>
            {t('admin.plans.replaceImage')}
          </Button>
        )}
        {plan.published ? null : (
          <Button variant="ghost" onClick={() => setConfirmDelete(true)}>
            {t('admin.plans.delete')}
          </Button>
        )}
      </div>

      {problem ? <p className="text-adminSmall text-danger-text pt-2">{t(problem.i18nKey)}</p> : null}

      {renaming ? (
        <Dialog
          title={t('admin.plans.renameTitle')}
          confirmLabel={t('common.save')}
          onConfirm={() => rename.mutate()}
          onClose={() => setRenaming(false)}
          busy={rename.isPending}
          disabled={name.trim().length === 0}
        >
          <Field label={t('admin.plans.floorName')} hint={t('admin.plans.renameHint')}>
            <input
              value={name}
              onChange={(event) => setName(event.target.value)}
              maxLength={120}
              className={fieldClass}
            />
          </Field>
        </Dialog>
      ) : null}

      {confirmDelete ? (
        <Dialog
          title={t('admin.plans.deleteTitle')}
          intro={t('admin.plans.deleteBody', { name: cardName })}
          tone="danger"
          confirmLabel={t('admin.plans.delete')}
          onConfirm={() => remove.mutate()}
          onClose={() => setConfirmDelete(false)}
          busy={remove.isPending}
        />
      ) : null}
    </Card>
  );
}

export type PlanCardState = 'no_image' | 'draft_only' | 'published' | 'published_with_draft';

export function planState(plan: {
  hasImage: boolean;
  published: unknown | null;
  draft: unknown | null;
}): PlanCardState {
  if (!plan.hasImage) return 'no_image';
  if (plan.published && plan.draft) return 'published_with_draft';
  return plan.published ? 'published' : 'draft_only';
}

function Chip({ state }: { state: PlanCardState }) {
  const { t } = useTranslation();
  const key = {
    no_image: 'admin.plans.stateNoImage',
    draft_only: 'admin.plans.stateDraft',
    published: 'admin.plans.statePublished',
    published_with_draft: 'admin.plans.statePublished',
  }[state];

  return (
    <span className="bg-fog font-body text-text rounded-full px-3 py-1 text-xs font-semibold">{t(key)}</span>
  );
}

/**
 * § 5.3 — the estimate is drawn behind the real bookings, never instead of
 * them, so a day that is already half booked cannot look free.
 */
function Calendar({ bars }: { bars: CalendarBar[] }) {
  const max = Math.max(1, ...bars.map((bar) => bar.total));

  return (
    <div className="flex items-end gap-2" role="img">
      {bars.map((bar) => (
        <div key={bar.day} className="flex flex-1 flex-col items-center gap-1">
          <span className="font-body text-ink text-xs tabular-nums">{bar.total}</span>
          <div
            className={`relative w-full rounded-t ${bar.free ? 'bg-lime' : 'bg-teal/40'}`}
            style={{ height: `${(bar.total / max) * 120 + 4}px` }}
          >
            {bar.booked > 0 ? (
              <div
                className="bg-teal absolute bottom-0 w-full rounded-t"
                style={{ height: `${(bar.booked / max) * 120 + 4}px` }}
              />
            ) : null}
          </div>
          <span className="font-body text-muted text-[10px]">{formatDayShort(bar.day)}</span>
        </div>
      ))}
    </div>
  );
}

function Legend({ className, label }: { className: string; label: string }) {
  return (
    <span className="flex items-center gap-1">
      <span className={`inline-block h-3 w-3 rounded ${className}`} />
      {label}
    </span>
  );
}

function shift(day: string, delta: number): string {
  const date = new Date(`${day}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() + delta);
  return date.toISOString().slice(0, 10);
}

/**
 * "22 FlexDesks · 1 Meetingraum · 5 Spinde · 4 Parkplätze" (§ 5.1).
 *
 * Only the kinds this plan actually has, in the order the board lists them —
 * a plan with no lockers should not say "0 Spinde", and the order is what
 * makes three cards scannable side by side.
 */
const KIND_ORDER = ['desk', 'room', 'locker', 'parking', 'office'] as const;

export function inventoryLine(inventory: Record<string, number>, t: TFunction): string {
  return KIND_ORDER.filter((kind) => (inventory[kind] ?? 0) > 0)
    .map((kind) => t(`admin.plans.kind.${kind}`, { count: inventory[kind] ?? 0 }))
    .join(' · ');
}
